import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { QuickPromptBar } from '../QuickPromptBar';
import { useProject } from '../../contexts/ProjectContext';
import { useTopicWorkspace } from '../../contexts/TopicWorkspaceContext';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { useNetworkEnabled } from '../../hooks/useNetworkEnabled';
import LoadingSpinner from '../LoadingSpinner';
import SerpModal from '../SerpModal';
import { EngineIcon, ExternalSearchLink, RefreshGlyph, formatSerpDate } from '../common/SerpHelpers';
import StageModelsWidget from '../common/StageModelsWidget';
import Internals from '../common/Internals';
import {
  AgentErrorBanner,
  AgentLoadError,
  AgentStatusChip,
} from '../common/AgentErrorBanner';
import { useShowInternals } from '../../contexts/InternalsContext';
import ChatMarkdown from '../common/ChatMarkdown';
import ContextUsageChip from '../ContextUsageChip';
import type {
  DiscoverKeywordsChatResponse,
  DiscoveryIdeaStatusFilter,
  GoogleSeedCandidate,
  GoogleSeedGroup,
  IdeaSource,
  RecTopicKeyword,
  TopicLabel,
  WebSocketEvent,
} from '../../types';

const SOURCE_META: Record<
  IdeaSource,
  { label: string; short: string; cls: string }
> = {
  paste_match: { label: 'Paste', short: 'Paste', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300' },
  store_match: { label: 'Store', short: 'Store', cls: 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-300' },
  ai_suggestion: {
    label: 'AI generated',
    short: 'AI',
    cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  },
  google_suggestion: {
    label: 'Google',
    short: 'Google',
    cls: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
  },
  agent_curated: {
    label: 'AI generated',
    short: 'AI',
    cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  },
  agent_generated: {
    label: 'AI generated',
    short: 'AI',
    cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
  },
};

type SourceFilterId = IdeaSource;

const SOURCE_FILTERS: { id: SourceFilterId; label: string }[] = [
  { id: 'paste_match', label: 'Paste' },
  { id: 'store_match', label: 'Store' },
  { id: 'google_suggestion', label: 'Google' },
  { id: 'ai_suggestion', label: 'AI generated' },
];

type ReviewedFilter = 'all' | 'endorsed' | 'rejected' | 'not_reviewed';

// One consolidated "Show" filter that replaces the two overlapping axes
// (status = approve/dismiss decision, reviewed = endorse/reject triage). Each
// option is a single pipeline stage and maps to a (status, reviewed) pair the
// backend already understands. Mutually exclusive — a row sits in exactly one.
type FeedFilter = 'all' | 'pending' | 'endorsed' | 'rejected' | 'dismissed';

const FEED_OPTIONS: {
  id: FeedFilter;
  label: string;
  status: DiscoveryIdeaStatusFilter;
  reviewed: ReviewedFilter;
}[] = [
  { id: 'pending', label: 'Pending', status: 'pending', reviewed: 'not_reviewed' },
  { id: 'endorsed', label: 'Endorsed', status: 'pending', reviewed: 'endorsed' },
  { id: 'rejected', label: 'Rejected', status: 'pending', reviewed: 'rejected' },
  { id: 'dismissed', label: 'Dismissed', status: 'dismissed', reviewed: 'all' },
  { id: 'all', label: 'All', status: 'all', reviewed: 'all' },
];

const AI_LOADING_MESSAGES = [
  'Asking the AI for fresh ideas...',
  'Brainstorming angles...',
  'Sifting the keyword store...',
  'Scoring by topic fit...',
];

// Display order + labels for the Google seed-preview popover. Order matters:
// the high-signal groups come first, raw Google suggestions last. Every
// section carries a hint so it's clear what it is and why it's on/off.
const SEED_GROUP_META: { id: GoogleSeedGroup; label: string; hint: string }[] = [
  { id: 'accepted', label: 'Accepted', hint: 'already in this topic' },
  { id: 'endorsed', label: 'Endorsed', hint: 'the agent flagged these as fits' },
  { id: 'yours', label: 'Your pastes & AI', hint: 'ideas you pasted or AI-generated' },
  {
    id: 'google',
    label: 'Google suggestions',
    hint: 'from earlier runs - off by default',
  },
];

// One toggleable keyword chip in the Google seed-preview popover.
function SeedChip({
  c,
  on,
  onToggle,
}: {
  c: GoogleSeedCandidate;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`px-2 py-0.5 rounded-full border text-[11px] ${
        on
          ? 'border-indigo-300 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300'
          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
      }`}
      title={c.already_sent ? 'Already sent - re-sends if selected' : undefined}
    >
      {on ? '✓ ' : ''}
      {c.value}
      {c.already_sent && (
        <span className="ml-1 text-[9px] text-amber-600 dark:text-amber-400">sent</span>
      )}
    </button>
  );
}

function SourceChip({ source }: { source: IdeaSource | null }) {
  const meta = source ? SOURCE_META[source] : null;
  return (
    <>
      {source && !meta && (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
          {source}
        </span>
      )}
      {meta && (
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded ${meta.cls}`}
          title={meta.label}
        >
          {meta.short}
        </span>
      )}
    </>
  );
}

// Manual review-state control: move a row between endorsed / rejected /
// pending by hand (the same axis the agent's triage sets). Clicking an active
// button clears it back to pending; clicking the other switches. Two buttons
// cover all three states.
// Per-row action group: Endorse | Reject | Accept | Dismiss in one segmented
// control. Endorse/Reject are the (toggleable) review state — clicking an
// active one clears it back to pending. Accept (into the topic) and Dismiss
// are the terminal user decisions. Dismiss takes no reason.
function RowActions({
  endorsed,
  rejected,
  disabled,
  onSetReview,
  onAccept,
  onDismiss,
}: {
  endorsed: boolean;
  rejected: boolean;
  disabled?: boolean;
  onSetReview: (state: 'endorsed' | 'rejected' | 'pending') => void;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const base = 'px-1.5 py-0.5 text-[10px] disabled:opacity-50';
  const divider = 'border-l border-gray-300 dark:border-gray-600';
  return (
    <span className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
      <button
        type="button"
        disabled={disabled}
        onClick={onAccept}
        title="Accept into the topic"
        className={`${base} bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30`}
      >
        Accept
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSetReview(endorsed ? 'pending' : 'endorsed')}
        title={endorsed ? 'Endorsed - click to clear to pending' : 'Endorse'}
        className={`${base} ${divider} ${
          endorsed
            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 font-medium'
            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        Endorse
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSetReview(rejected ? 'pending' : 'rejected')}
        title={rejected ? 'Rejected - click to clear to pending' : 'Reject'}
        className={`${base} ${divider} ${
          rejected
            ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 font-medium'
            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        Reject
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={onDismiss}
        title="Dismiss"
        className={`${base} ${divider} bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800`}
      >
        Dismiss
      </button>
    </span>
  );
}

function MetricsBlock({ idea }: { idea: RecTopicKeyword }) {
  const hasMetrics =
    idea.avg_monthly_searches != null ||
    idea.competition_level;
  if (!hasMetrics) {
    return (
      <span className="text-xs text-gray-400 dark:text-gray-500 italic">metrics pending...</span>
    );
  }
  return (
    /* `whitespace-nowrap` per metric, because the label and its number are one fact.
       Without it a narrow column broke at the space after the colon and the row read
       "vol:  comp:" over "260  HIGH" — two columns of nothing lining up. */
    <div className="flex flex-wrap gap-x-3 text-xs text-gray-600 dark:text-gray-300">
      {idea.avg_monthly_searches != null && (
        <span className="whitespace-nowrap">
          vol: {idea.avg_monthly_searches.toLocaleString()}
        </span>
      )}
      {idea.competition_level && (
        <span className="whitespace-nowrap">comp: {idea.competition_level}</span>
      )}
    </div>
  );
}

export default function DiscoveryPane() {
  const { selectedTopic } = useProject();
  const { triggerLinkedRefresh } = useTopicWorkspace();
  const { subscribe } = useWebSocket();
  const networkEnabled = useNetworkEnabled();

  const [inputText, setInputText] = useState('');
  const [matching, setMatching] = useState(false);
  const [topicLabels, setTopicLabels] = useState<TopicLabel[]>([]);

  const [ideas, setIdeas] = useState<RecTopicKeyword[]>([]);
  const [serpModalKeyword, setSerpModalKeyword] = useState<{ id: number; value: string } | null>(null);
  const [requestingSerpFor, setRequestingSerpFor] = useState<number | null>(null);
  const [highlightedIdeaId, setHighlightedIdeaId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [sourceFilters, setSourceFilters] = useState<SourceFilterId[]>([]);
  const [search, setSearch] = useState('');
  // The two underlying axes are driven together by the single "Show" control
  // (see FEED_OPTIONS). Default pair = the "Pending" stage: untriaged
  // candidates still in the pool (not approved/dismissed, not endorsed/rejected).
  const [statusFilter, setStatusFilter] = useState<DiscoveryIdeaStatusFilter>('pending');
  const [reviewedFilter, setReviewedFilter] = useState<ReviewedFilter>('not_reviewed');
  const [googleStatus, setGoogleStatus] = useState<
    'pending' | 'ready' | 'skipped' | 'no_config' | null
  >(null);
  const [googleMessage, setGoogleMessage] = useState('');
  const [actingOn, setActingOn] = useState<Set<number>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkActing, setBulkActing] = useState(false);

  // Chat planner state — replaces the old one-shot runGuidance +
  // Refine button. Lives in a right-side pane next to the ideas list.
  const [chat, setChat] = useState<DiscoverKeywordsChatResponse | null>(null);
  const [chatLoadError, setChatLoadError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [cancellingTurn, setCancellingTurn] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);

  const fetchIdeas = useCallback(async () => {
    if (!selectedTopic) {
      setIdeas([]);
      return;
    }
    setLoading(true);
    try {
      // Collapse ai_suggestion + agent_generated + agent_curated into one filter group
      const expandedSources = sourceFilters.flatMap((s) =>
        s === 'ai_suggestion' || s === 'agent_generated'
          ? ['ai_suggestion' as IdeaSource, 'agent_generated' as IdeaSource, 'agent_curated' as IdeaSource]
          : [s]
      );
      const uniqueSources = Array.from(new Set(expandedSources));

      const resp = await api.listDiscoveryIdeas(selectedTopic.id, {
        status: statusFilter,
        sources: uniqueSources.length ? uniqueSources : undefined,
        reviewed: reviewedFilter === 'all' ? undefined : reviewedFilter,
        sort_by: 'fit',
        sort_order: 'desc',
        page: 1,
        size: 200,
      });
      setIdeas(resp.items);
    } catch (err) {
      console.error('Failed to load discovery ideas:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedTopic, statusFilter, sourceFilters, reviewedFilter]);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  // Coalesce bursts of websocket events (the agent emits one
  // `discovery_idea_added` per keyword — dozens in a few seconds)
  // into a single list refresh. Each event resets a short quiet
  // window; a max-wait guarantees a refresh at least every ~1.5s so
  // a long continuous stream still updates instead of going silent.
  const fetchIdeasRef = useRef(fetchIdeas);
  useEffect(() => {
    fetchIdeasRef.current = fetchIdeas;
  }, [fetchIdeas]);
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchMaxWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleFetchIdeas = useCallback(() => {
    const run = () => {
      if (fetchDebounceRef.current) {
        clearTimeout(fetchDebounceRef.current);
        fetchDebounceRef.current = null;
      }
      if (fetchMaxWaitRef.current) {
        clearTimeout(fetchMaxWaitRef.current);
        fetchMaxWaitRef.current = null;
      }
      fetchIdeasRef.current();
    };
    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
    fetchDebounceRef.current = setTimeout(run, 400);
    if (!fetchMaxWaitRef.current) fetchMaxWaitRef.current = setTimeout(run, 1500);
  }, []);
  useEffect(
    () => () => {
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
      if (fetchMaxWaitRef.current) clearTimeout(fetchMaxWaitRef.current);
    },
    []
  );

  useEffect(() => {
    if (!selectedTopic) return;
    api
      .getTopicLabels(selectedTopic.id)
      .then((resp) => setTopicLabels(resp.labels.filter((l) => l.is_active)))
      .catch(() => setTopicLabels([]));
  }, [selectedTopic]);

  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (!selectedTopic) return;
      if (
        event.event_type === 'keyword_recommendations_ready' &&
        event.topic_id === selectedTopic.id
      ) {
        setGoogleStatus(event.status);
        setGoogleMessage(event.message);
        scheduleFetchIdeas();
      } else if (
        event.event_type === 'discovery_idea_added' &&
        event.topic_id === selectedTopic.id
      ) {
        scheduleFetchIdeas();
      } else if (
        event.event_type === 'discover_keywords_chat' &&
        event.topic_id === selectedTopic.id
      ) {
        // Chat-turn lifecycle. Refresh the chat (so messages /
        // status flip) and the ideas list (the agent likely
        // endorsed / rejected rows during the turn).
        refreshChat();
        if (event.status === 'replied' || event.status === 'failed') {
          scheduleFetchIdeas();
        }
      } else if (event.event_type === 'serp_requested') {
        const ids = new Set(event.keyword_ids);
        setIdeas(prev => prev.map(i =>
          ids.has(i.keyword_id) ? { ...i, has_pending_serp_request: true } : i
        ));
      } else if (event.event_type === 'serp_completion') {
        scheduleFetchIdeas();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, selectedTopic, scheduleFetchIdeas]);

  // Keep the selection in sync with what's actually visible+selectable. When a
  // filter or the search hides a selected row (or a row leaves the pending
  // set), drop it from the selection so the "N selected" count and bulk
  // actions only ever reflect rows you can see.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const term = search.trim().toLowerCase();
      const selectable = new Set(
        ideas
          .filter((i) => !i.approved_at && !i.dismissed_at)
          .filter((i) => !term || i.keyword_value.toLowerCase().includes(term))
          .map((i) => i.id),
      );
      const kept = Array.from(prev).filter((id) => selectable.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [ideas, search]);

  const handleAddFromLabels = () => {
    const labelValues = topicLabels.map((l) => l.label_value);
    setInputText((prev) => {
      const trimmed = prev.trimEnd();
      const joined = labelValues.join('\n');
      return trimmed ? trimmed + '\n' + joined : joined;
    });
  };

  const handleCheck = async () => {
    if (!selectedTopic || !inputText.trim()) return;
    const keywords = inputText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (keywords.length === 0) return;

    setMatching(true);
    setGoogleStatus(null);
    setGoogleMessage('');
    try {
      const resp = await api.matchKeywordsAgainstStore(selectedTopic.id, keywords);
      if (resp.recommendation_id) {
        setGoogleStatus('pending');
      }
      await fetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to check keywords');
    } finally {
      setMatching(false);
    }
  };

  const [fetchingGoogle, setFetchingGoogle] = useState(false);

  // Google seed-preview popover. "Refresh Google Ideas" no longer fires
  // immediately — it opens a confirm step so the user sees (and can adjust)
  // exactly which keywords seed the run. Raw Google suggestions from prior
  // runs are off by default (the endorsement gate) so they don't re-seed.
  const [seedPreviewOpen, setSeedPreviewOpen] = useState(false);
  const [seedCandidates, setSeedCandidates] = useState<GoogleSeedCandidate[] | null>(
    null,
  );
  const [seedSelected, setSeedSelected] = useState<Set<number>>(new Set());
  const [seedLoading, setSeedLoading] = useState(false);
  // True when the preview was limited to the rows the user checkbox-selected
  // on the left — drives a flat "selected only" view instead of the groups.
  const [seedRestricted, setSeedRestricted] = useState(false);

  const openSeedPreview = async () => {
    if (!selectedTopic) return;
    // If rows are selected in the discovery list, seed from only those.
    // selectedIds holds RecTopicKeyword (idea) ids — map them to keyword ids.
    const selectedKeywordIds = Array.from(
      new Set(
        ideas
          .filter((i) => selectedIds.has(i.id))
          .map((i) => i.keyword_id),
      ),
    );
    const restricted = selectedKeywordIds.length > 0;
    setSeedRestricted(restricted);
    setSeedPreviewOpen(true);
    setSeedLoading(true);
    setSeedCandidates(null);
    try {
      const resp = await api.previewGoogleIdeasSeeds(
        selectedTopic.id,
        restricted ? selectedKeywordIds : undefined,
      );
      setSeedCandidates(resp.candidates);
      setSeedSelected(
        new Set(
          resp.candidates.filter((c) => c.default_selected).map((c) => c.keyword_id),
        ),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load seed preview');
      setSeedPreviewOpen(false);
    } finally {
      setSeedLoading(false);
    }
  };

  const toggleSeed = (id: number) => {
    setSeedSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSeedGroup = (ids: number[], on: boolean) => {
    setSeedSelected((prev) => {
      const next = new Set(prev);
      if (on) ids.forEach((i) => next.add(i));
      else ids.forEach((i) => next.delete(i));
      return next;
    });
  };

  const confirmSendSeeds = async () => {
    if (!selectedTopic || seedSelected.size === 0 || fetchingGoogle) return;
    setFetchingGoogle(true);
    setGoogleStatus(null);
    setGoogleMessage('');
    try {
      const resp = await api.sendGoogleIdeasSeeds(
        selectedTopic.id,
        Array.from(seedSelected),
      );
      if (resp.status === 'queued' && resp.recommendation_id) {
        setGoogleStatus('pending');
        setGoogleMessage(`Sending ${resp.keywords_sent} keyword(s) to Google`);
        setSeedPreviewOpen(false);
      } else {
        setGoogleStatus('skipped');
        setGoogleMessage(resp.message || 'No keywords sent.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send to Google');
    } finally {
      setFetchingGoogle(false);
    }
  };

  // ── Chat planner handlers ─────────────────────────────────────────
  // Each user message kicks off one LLM round-trip; the agent writes
  // endorse / reject / add directly to RecTopicKeyword rows during
  // the turn. The discovery_idea_added WS stream refreshes the list
  // pane live; discover_keywords_chat lifecycle drives the chat pane.

  const refreshChat = useCallback(async () => {
    if (!selectedTopic) return;
    try {
      const next = await api.getDiscoverKeywordsChat(selectedTopic.id);
      setChat(next);
      setChatLoadError(null);
    } catch (err) {
      console.error('Failed to load discover-keywords chat:', err);
      setChatLoadError(
        err instanceof Error ? err.message : 'Failed to load chat.',
      );
    }
  }, [selectedTopic]);

  useEffect(() => {
    if (selectedTopic) refreshChat();
    else setChat(null);
  }, [selectedTopic, refreshChat]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages?.length, chat?.status]);

  const sendChatMessage = async (overrideText?: string) => {
    if (!selectedTopic) return;
    // Quick-prompt chips pass their text directly; the textarea path
    // falls back to the current draft.
    const text = (overrideText ?? chatInput).trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const next = await api.sendDiscoverKeywordsChatMessage(
        selectedTopic.id,
        text
      );
      setChat(next);
      setChatInput('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const cancelChatTurn = async () => {
    if (!selectedTopic || cancellingTurn) return;
    setCancellingTurn(true);
    try {
      await api.cancelDiscoverKeywordsChat(selectedTopic.id);
    } catch (err) {
      console.error('Failed to cancel turn:', err);
    } finally {
      setCancellingTurn(false);
    }
  };

  const startFreshChat = async () => {
    if (!selectedTopic || endingSession) return;
    if (
      !window.confirm('Start over? The chat context is cleared.')
    ) {
      return;
    }
    setEndingSession(true);
    try {
      const next = await api.endDiscoverKeywordsChatSession(selectedTopic.id);
      setChat(next);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start fresh chat');
    } finally {
      setEndingSession(false);
    }
  };


  const resetChat = async () => {
    if (!selectedTopic || resetting) return;
    if (
      !window.confirm('Reset? This destroys the chat history.')
    ) {
      return;
    }
    setResetting(true);
    try {
      const next = await api.resetDiscoverKeywordsChat(selectedTopic.id);
      setChat(next);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const clearStuckThinking = async () => {
    if (!selectedTopic) return;
    if (
      !window.confirm(
        'Force-clear the "thinking" state? Use this if the previous request died. Auto-recovery kicks in after ~5 minutes.'
      )
    ) {
      return;
    }
    try {
      const next = await api.clearDiscoverKeywordsChatThinking(selectedTopic.id);
      setChat(next);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to clear');
    }
  };

  const markActing = (id: number, acting: boolean) => {
    setActingOn((prev) => {
      const next = new Set(prev);
      acting ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const handleAccept = async (idea: RecTopicKeyword) => {
    markActing(idea.id, true);
    try {
      await api.acceptDiscoveryIdea(idea.id);
      triggerLinkedRefresh();
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(idea.id); return next; });
      setIdeas((prev) =>
        statusFilter === 'pending'
          ? prev.filter((i) => i.id !== idea.id)
          : prev.map((i) =>
              i.id === idea.id
                ? { ...i, approved_at: new Date().toISOString() }
                : i
            )
      );
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to accept');
    } finally {
      markActing(idea.id, false);
    }
  };

  const handleDismiss = async (idea: RecTopicKeyword) => {
    markActing(idea.id, true);
    try {
      await api.dismissDiscoveryIdea(idea.id);
      setSelectedIds((prev) => { const next = new Set(prev); next.delete(idea.id); return next; });
      setIdeas((prev) =>
        statusFilter === 'pending'
          ? prev.filter((i) => i.id !== idea.id)
          : prev.map((i) =>
              i.id === idea.id
                ? { ...i, dismissed_at: new Date().toISOString() }
                : i
            )
      );
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to dismiss');
    } finally {
      markActing(idea.id, false);
    }
  };

  const handleUndismiss = async (idea: RecTopicKeyword) => {
    markActing(idea.id, true);
    try {
      await api.undismissDiscoveryIdea(idea.id);
      // In the Dismissed view the restored row no longer belongs; elsewhere it
      // just returns to pending. The refetch reconciles either way.
      setIdeas((prev) =>
        statusFilter === 'dismissed'
          ? prev.filter((i) => i.id !== idea.id)
          : prev.map((i) =>
              i.id === idea.id ? { ...i, dismissed_at: null } : i
            )
      );
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to restore');
    } finally {
      markActing(idea.id, false);
    }
  };

  const handleSetReview = async (
    idea: RecTopicKeyword,
    state: 'endorsed' | 'rejected' | 'pending',
  ) => {
    markActing(idea.id, true);
    const now = new Date().toISOString();
    // Optimistic — flip the row's endorsed/rejected flags in place so the
    // toggle reflects the click immediately. A refetch then reconciles the
    // list against the active filters (the row drops out if it no longer
    // matches the current Status/Reviewed view).
    setIdeas((prev) =>
      prev.map((i) =>
        i.id === idea.id
          ? {
              ...i,
              endorsed_at: state === 'endorsed' ? now : null,
              rejected_at: state === 'rejected' ? now : null,
            }
          : i,
      ),
    );
    try {
      await api.reviewDiscoveryIdea(idea.id, state);
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update review state');
      fetchIdeas();
    } finally {
      markActing(idea.id, false);
    }
  };

  // What actually renders. Always-on rules:
  //  - drop approved/linked rows entirely — managed elsewhere, no actions here.
  //  - drop dismissed rows from the active views (clutter), but KEEP them in
  //    the dedicated "Dismissed" view, where they can be restored to pending.
  //  - apply the search-box substring filter.
  // The chat's pool counts below derive from the raw pool, not this.
  const searchTerm = search.trim().toLowerCase();
  const inDismissedView = statusFilter === 'dismissed';
  const visibleIdeas = ideas.filter(
    (i) =>
      !i.approved_at &&
      (inDismissedView || !i.dismissed_at) &&
      (!searchTerm || i.keyword_value.toLowerCase().includes(searchTerm)),
  );
  const pendingIdeas = visibleIdeas.filter((i) => !i.dismissed_at);

  // Pool counts for the chat's quick-prompt heuristic (expansion vs. triage) —
  // derived from the full pool, independent of the search box.
  const poolPendingCount = ideas.filter(
    (i) => !i.approved_at && !i.dismissed_at
  ).length;
  const endorsedPendingCount = ideas.filter(
    (i) => !i.approved_at && !i.dismissed_at && i.endorsed_at
  ).length;

  const lastClickedIndex = useRef<number | null>(null);

  const toggleSelected = (id: number, index: number, shiftKey: boolean) => {
    if (shiftKey && lastClickedIndex.current !== null) {
      // Range select between last clicked and current
      const start = Math.min(lastClickedIndex.current, index);
      const end = Math.max(lastClickedIndex.current, index);
      const rangeIds = pendingIdeas.slice(start, end + 1).map(i => i.id);
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (const rid of rangeIds) next.add(rid);
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }
    lastClickedIndex.current = index;
  };

  const toggleSelectAll = () => {
    const allPendingIds = new Set(pendingIdeas.map(i => i.id));
    const allSelected = pendingIdeas.length > 0 && pendingIdeas.every(i => selectedIds.has(i.id));
    setSelectedIds(allSelected ? new Set() : allPendingIds);
  };

  const handleBulkAccept = async () => {
    const ids = Array.from(selectedIds);
    setBulkActing(true);
    try {
      await Promise.all(ids.map(id => api.acceptDiscoveryIdea(id)));
      triggerLinkedRefresh();
      setIdeas(prev =>
        statusFilter === 'pending'
          ? prev.filter(i => !selectedIds.has(i.id))
          : prev.map(i =>
              selectedIds.has(i.id) ? { ...i, approved_at: new Date().toISOString() } : i
            )
      );
      setSelectedIds(new Set());
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to accept');
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkReview = async (
    state: 'endorsed' | 'rejected' | 'pending',
  ) => {
    const ids = Array.from(selectedIds);
    setBulkActing(true);
    const now = new Date().toISOString();
    setIdeas((prev) =>
      prev.map((i) =>
        selectedIds.has(i.id)
          ? {
              ...i,
              endorsed_at: state === 'endorsed' ? now : null,
              rejected_at: state === 'rejected' ? now : null,
            }
          : i,
      ),
    );
    try {
      await Promise.all(ids.map((id) => api.reviewDiscoveryIdea(id, state)));
      setSelectedIds(new Set());
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update review state');
      fetchIdeas();
    } finally {
      setBulkActing(false);
    }
  };

  const handleBulkDismiss = async () => {
    const ids = Array.from(selectedIds);
    setBulkActing(true);
    try {
      await Promise.all(ids.map(id => api.dismissDiscoveryIdea(id)));
      setIdeas(prev =>
        statusFilter === 'pending'
          ? prev.filter(i => !selectedIds.has(i.id))
          : prev.map(i =>
              selectedIds.has(i.id)
                ? { ...i, dismissed_at: new Date().toISOString() }
                : i
            )
      );
      setSelectedIds(new Set());
      scheduleFetchIdeas();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to dismiss');
    } finally {
      setBulkActing(false);
    }
  };

  const handleRequestSerp = async (keywordId: number) => {
    setRequestingSerpFor(keywordId);
    try {
      await api.createSerpRequest(keywordId);
      setIdeas((prev) =>
        prev.map((i) =>
          i.keyword_id === keywordId ? { ...i, has_pending_serp_request: true } : i
        )
      );
    } catch (err) {
      console.error('Failed to request SERP:', err);
    } finally {
      setRequestingSerpFor(null);
    }
  };

  const toggleSource = (s: SourceFilterId) => {
    setSourceFilters((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  if (!selectedTopic) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
        Select a topic to discover keywords
      </div>
    );
  }

  return (
    // Two-pane layout matches the Knowledge planner's `grid-cols-2`
    // pattern — both sides get equal width so neither gets squeezed.
    // `min-w-0` on each cell lets internal content shrink instead of
    // forcing overflow at narrow slideout widths.
    // `grid-rows-1` is `minmax(0, 1fr)`, not `auto`: an auto row grows to its
    // tallest cell, so a long idea list would stretch the grid past the panel
    // and take the chat's prompt box down with it. Pinned to one full-height
    // row, each pane scrolls inside itself instead.
    <div className="h-full grid grid-cols-2 grid-rows-1">
      {/* Left pane: existing ideas list, filters, seed bar */}
      <div className="min-w-0 min-h-0 flex flex-col border-r border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Seed bar */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
            Paste a few ideas - we'll match them, fetch Google suggestions, and curate more
          </label>
          {topicLabels.length > 0 && (
            <button
              onClick={handleAddFromLabels}
              type="button"
              className="px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded hover:bg-purple-100 dark:hover:bg-purple-900/40"
            >
              + from labels ({topicLabels.length})
            </button>
          )}
        </div>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder={'best running shoes\nmarathon training tips'}
          rows={3}
          className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="flex items-center gap-2 mt-2 flex-shrink-0">
          <button
            onClick={handleCheck}
            disabled={matching || !inputText.trim()}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
            title="Match pasted keywords against the store and send to Google"
          >
            {matching ? 'Checking...' : 'Paste & Check'}
          </button>
          <button
            onClick={openSeedPreview}
            disabled={seedPreviewOpen || googleStatus === 'pending'}
            className="px-3 py-1.5 bg-orange-600 text-white text-sm font-medium rounded hover:bg-orange-700 disabled:opacity-50 whitespace-nowrap"
            title="Choose which keywords to send to Google Keyword Planner"
          >
            Refresh Google Ideas
          </button>
        </div>
        {seedPreviewOpen && (
          <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 shadow-sm p-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium text-gray-800 dark:text-gray-200">
                Seed Google Keyword Planner
              </span>
              <button
                type="button"
                onClick={() => setSeedPreviewOpen(false)}
                className="icon-btn text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                title="Cancel"
              >
                ✕
              </button>
            </div>
            {seedLoading ? (
              <div className="py-3 text-gray-500 dark:text-gray-400">Loading candidates…</div>
            ) : !seedCandidates || seedCandidates.length === 0 ? (
              <div className="py-3 text-gray-500 dark:text-gray-400">
                No keywords to seed yet - paste some ideas or accept candidates
                first.
              </div>
            ) : (
              <>
                <p className="text-gray-500 dark:text-gray-400 mb-2">
                  {seedRestricted
                    ? `Seeding from the ${seedCandidates.length} keyword(s) you selected in the list. Clear the selection on the left to choose from the whole pool.`
                    : "Pick which keywords to send. Google suggestions from earlier runs are off by default so they don't re-seed."}
                </p>
                <div className="max-h-64 overflow-auto space-y-2">
                  {seedRestricted ? (
                    // Selection-restricted: one flat "selected" section, no
                    // per-group gate (the explicit pick overrides it).
                    (() => {
                      const ids = seedCandidates.map((r) => r.keyword_id);
                      const allOn = ids.every((i) => seedSelected.has(i));
                      return (
                        <div>
                          <button
                            type="button"
                            onClick={() => toggleSeedGroup(ids, !allOn)}
                            className="text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-700 dark:hover:text-indigo-300 mb-1"
                          >
                            {allOn ? '☑' : '☐'} Selected from the list (
                            {seedCandidates.length})
                          </button>
                          <div className="flex flex-wrap gap-1 pl-1">
                            {seedCandidates.map((r) => (
                              <SeedChip
                                key={r.keyword_id}
                                c={r}
                                on={seedSelected.has(r.keyword_id)}
                                onToggle={() => toggleSeed(r.keyword_id)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    SEED_GROUP_META.map((g) => {
                      const rows = seedCandidates.filter((c) => c.group === g.id);
                      if (rows.length === 0) return null;
                      const ids = rows.map((r) => r.keyword_id);
                      const allOn = ids.every((i) => seedSelected.has(i));
                      return (
                        <div key={g.id}>
                          <div className="flex items-center gap-2 mb-1">
                            <button
                              type="button"
                              onClick={() => toggleSeedGroup(ids, !allOn)}
                              className="text-[11px] font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-700 dark:hover:text-indigo-300"
                            >
                              {allOn ? '☑' : '☐'} {g.label} ({rows.length})
                            </button>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                              {g.hint}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 pl-1">
                            {rows.map((r) => (
                              <SeedChip
                                key={r.keyword_id}
                                c={r}
                                on={seedSelected.has(r.keyword_id)}
                                onToggle={() => toggleSeed(r.keyword_id)}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">
                    {
                      Array.from(seedSelected).filter(
                        (id) =>
                          seedCandidates.find((c) => c.keyword_id === id)
                            ?.already_sent,
                      ).length
                    }{' '}
                    already-sent will be re-sent
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSeedPreviewOpen(false)}
                      className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmSendSeeds}
                      disabled={fetchingGoogle || seedSelected.size === 0}
                      className="px-3 py-1 text-xs font-medium text-white bg-orange-600 rounded hover:bg-orange-700 disabled:opacity-50"
                    >
                      {fetchingGoogle
                        ? 'Sending…'
                        : `Send ${seedSelected.size} to Google`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {(googleStatus === 'pending' || googleStatus === 'skipped' || googleStatus === 'no_config') && (
          <div className="flex items-center gap-3 mt-1.5 text-xs">
            {googleStatus === 'pending' && (
              <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                <svg
                  className="animate-spin h-3 w-3 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Google Keyword Planner...
              </span>
            )}
            {(googleStatus === 'skipped' || googleStatus === 'no_config') && (
              <span className="text-amber-600 dark:text-amber-400">{googleMessage}</span>
            )}
          </div>
        )}
        {googleStatus === 'ready' && googleMessage && (
          <div className="mt-1.5 flex items-start gap-2 text-xs text-orange-900 dark:text-orange-200 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded px-2 py-1.5">
            <span aria-hidden className="mt-0.5">✓</span>
            <span className="flex-1 whitespace-pre-wrap">{googleMessage}</span>
            <button
              onClick={() => {
                setGoogleStatus(null);
                setGoogleMessage('');
              }}
              className="icon-btn text-orange-800 dark:text-orange-300 hover:text-orange-950"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-x-4 gap-y-2 flex-shrink-0">
        <div className="relative flex items-center">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter keywords…"
            className="w-44 pl-2 pr-6 py-0.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              title="Clear filter"
              className="icon-btn absolute right-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              ✕
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Show:</span>
          <div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            {FEED_OPTIONS.map((opt, i) => {
              const on =
                statusFilter === opt.status && reviewedFilter === opt.reviewed;
              return (
                <button
                  key={opt.id}
                  onClick={() => {
                    setStatusFilter(opt.status);
                    setReviewedFilter(opt.reviewed);
                  }}
                  className={`px-2 py-0.5 text-xs ${
                    on
                      ? 'bg-gray-800 text-white font-medium'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  } ${i > 0 ? 'border-l border-gray-300 dark:border-gray-600' : ''}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Sources:</span>
          {SOURCE_FILTERS.map((s) => {
            const on = sourceFilters.includes(s.id);
            const activeCls =
              s.id in SOURCE_META
                ? (SOURCE_META[s.id]?.cls || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200') + ' ring-1 ring-current'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 ring-1 ring-current';
            return (
              <button
                key={s.id}
                onClick={() => toggleSource(s.id)}
                className={`px-2 py-0.5 text-xs rounded ${
                  on ? activeCls : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {s.label}
              </button>
            );
          })}
          {sourceFilters.length > 0 && (
            <button
              onClick={() => setSourceFilters([])}
              className="px-2 py-0.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              clear
            </button>
          )}
        </div>
      </div>

      {/* Select-all header (pinned) */}
      {ideas.length > 0 && pendingIdeas.length > 0 && (
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
          <input
            type="checkbox"
            checked={pendingIdeas.length > 0 && pendingIdeas.every(i => selectedIds.has(i.id))}
            onChange={toggleSelectAll}
            className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
          </span>
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={handleBulkAccept}
                disabled={bulkActing}
                className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {bulkActing ? '...' : `Accept ${selectedIds.size}`}
              </button>
              <button
                onClick={() => handleBulkReview('endorsed')}
                disabled={bulkActing}
                className="px-2 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 rounded hover:bg-emerald-200 dark:hover:bg-emerald-900/50 disabled:opacity-50"
              >
                {bulkActing ? '...' : `Endorse ${selectedIds.size}`}
              </button>
              <button
                onClick={() => handleBulkReview('rejected')}
                disabled={bulkActing}
                className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
              >
                {bulkActing ? '...' : `Reject ${selectedIds.size}`}
              </button>
              <button
                onClick={() => handleBulkReview('pending')}
                disabled={bulkActing}
                title="Clear endorsed/rejected back to pending (terminal state untouched)"
                className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {bulkActing ? '...' : `Pending ${selectedIds.size}`}
              </button>
              <button
                onClick={handleBulkDismiss}
                disabled={bulkActing}
                className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                {bulkActing ? '...' : `Dismiss ${selectedIds.size}`}
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ml-auto"
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {/* Feed */}
      <div className="flex-1 min-h-0 overflow-auto">
        {loading && ideas.length === 0 ? (
          <div className="p-8 flex items-center justify-center">
            <LoadingSpinner messages={AI_LOADING_MESSAGES} />
          </div>
        ) : ideas.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No ideas yet - paste seeds above, fetch Google ideas, or refine with AI.
          </div>
        ) : visibleIdeas.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {search ? `No keywords match “${search}”.` : 'Nothing to review here.'}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {visibleIdeas.map((idea) => {
              const isActing = actingOn.has(idea.id);
              const isApproved = !!idea.approved_at;
              const isDismissed = !!idea.dismissed_at;
              const pendingIndex = !isApproved && !isDismissed ? pendingIdeas.indexOf(idea) : -1;
              return (
                <li
                  key={idea.id}
                  /* WRAPS, rather than squeezing. The action group is a fixed ~230px of
                     segmented buttons; beside it in a half-width pane the keyword had
                     about ninety pixels left, so "sunscreen for eczema prone skin" came
                     out as three stacked words with the metrics wrapping mid-label under
                     it. `flex-wrap` plus a real flex-basis on the text column means the
                     buttons drop to their own line the moment both cannot fit — one rule,
                     no breakpoint to keep in sync with the pane's own resizing. */
                  className={`px-3 py-2 flex flex-wrap items-start gap-x-3 gap-y-2 ${
                    highlightedIdeaId === idea.id
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 ring-2 ring-indigo-400'
                      : isApproved
                      ? 'bg-green-50 dark:bg-green-900/30'
                      : isDismissed
                      ? 'bg-gray-50 dark:bg-gray-800 opacity-60'
                      : selectedIds.has(idea.id)
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  {!isApproved && !isDismissed && (
                    <input
                      type="checkbox"
                      checked={selectedIds.has(idea.id)}
                      onClick={(e) => toggleSelected(idea.id, pendingIndex, e.shiftKey)}
                      readOnly
                      className="mt-1 h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 flex-shrink-0 cursor-pointer"
                    />
                  )}
                  {/* basis-56 is what makes the wrap happen: with `min-w-0` alone the
                      column shrinks to nothing and the buttons never move down. min-w-0
                      stays so the text can still truncate once it HAS the line. */}
                  <div className="flex-1 basis-56 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {idea.keyword_value}
                      </span>
                      {idea.latest_serp_date ? (
                        <button
                          onClick={() => { setHighlightedIdeaId(idea.id); setSerpModalKeyword({ id: idea.keyword_id, value: idea.keyword_value }); }}
                          className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                          title={`View ${idea.serp_count} SERP result(s)`}
                        >
                          <EngineIcon engine={idea.latest_serp_engine} size="sm" />
                          <span>{formatSerpDate(idea.latest_serp_date)}</span>
                        </button>
                      ) : idea.has_pending_serp_request ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                          <RefreshGlyph spinning /><span>Pending</span>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRequestSerp(idea.keyword_id)}
                          disabled={requestingSerpFor === idea.keyword_id || !networkEnabled}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={networkEnabled ? undefined : 'Network access is disabled. Enable it in Network Settings to fetch SERPs.'}
                        >
                          {requestingSerpFor === idea.keyword_id ? '...' : 'Fetch SERP'}
                        </button>
                      )}
                      <ExternalSearchLink engine={idea.latest_serp_engine} keyword={idea.keyword_value} onClick={() => setHighlightedIdeaId(idea.id)} />
                      <SourceChip source={idea.source} />
                      {idea.fit_score != null && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                          fit {Math.round(idea.fit_score * 100)}
                        </span>
                      )}
                      {idea.created_at && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                          {formatSerpDate(idea.created_at)}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5">
                      <MetricsBlock idea={idea} />
                    </div>
                    {idea.reason && (
                      /* Two lines, not one truncated one. This is the agent's REASON
                         for putting the row in front of you — "Initial seed cov…" tells
                         you nothing, and deciding without it means opening something. */
                      <div
                        title={idea.reason}
                        className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic line-clamp-2"
                      >
                        {idea.reason}
                      </div>
                    )}
                    {idea.dismiss_reason && (
                      <div className="mt-1 text-xs text-red-500">
                        dismissed: {idea.dismiss_reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 relative ml-auto">
                    {isApproved ? (
                      <span className="text-xs text-green-700 dark:text-green-300">Linked</span>
                    ) : isDismissed ? (
                      <button
                        onClick={() => handleUndismiss(idea)}
                        disabled={isActing}
                        title="Restore to pending"
                        className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                      >
                        {isActing ? '...' : '↩ Restore'}
                      </button>
                    ) : (
                      <RowActions
                        endorsed={!!idea.endorsed_at}
                        rejected={!!idea.rejected_at}
                        disabled={isActing}
                        onSetReview={(s) => handleSetReview(idea, s)}
                        onAccept={() => handleAccept(idea)}
                        onDismiss={() => handleDismiss(idea)}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      </div>

      {/* Right pane: chat with the discover-keywords agent. Each user
          message kicks off one LLM round-trip; the agent writes
          endorse / reject / add directly to RecTopicKeyword rows and
          they appear in the list pane on the left via the
          discovery_idea_added stream. */}
      <DiscoverChatPane
        chat={chat}
        chatLoadError={chatLoadError}
        onRetryChatLoad={refreshChat}
        chatInput={chatInput}
        setChatInput={setChatInput}
        sending={sending}
        endingSession={endingSession}
        resetting={resetting}
        cancellingTurn={cancellingTurn}
        pendingCount={poolPendingCount}
        endorsedPendingCount={endorsedPendingCount}
        topicLabel={selectedTopic.name?.trim() || 'this topic'}
        onSend={sendChatMessage}
        onCancelTurn={cancelChatTurn}
        onStartFresh={startFreshChat}
        onReset={resetChat}
        onClearThinking={clearStuckThinking}
        chatBottomRef={chatBottomRef}
      />

      {serpModalKeyword && (
        <SerpModal
          isOpen={true}
          onClose={() => setSerpModalKeyword(null)}
          keywordId={serpModalKeyword.id}
          keywordValue={serpModalKeyword.value}
        />
      )}
    </div>
  );
}


// ============================================================================
// DiscoverChatPane — right-pane chat for the discover-keywords planner.
// Mirrors the look/feel of KnowledgePanel + StructurePlanPanel chat panes.
// ============================================================================

interface DiscoverChatPaneProps {
  chat: DiscoverKeywordsChatResponse | null;
  chatLoadError: string | null;
  onRetryChatLoad: () => void;
  chatInput: string;
  setChatInput: (v: string) => void;
  sending: boolean;
  endingSession: boolean;
  resetting: boolean;
  cancellingTurn: boolean;
  // Current-view pool counts — drive the static quick-prompt set when the
  // agent hasn't proposed its own follow-ups.
  pendingCount: number;
  endorsedPendingCount: number;
  // Topic name, woven into the relevance quick-prompts (e.g. "Endorse the
  // keywords that are strictly about Bags & Packs").
  topicLabel: string;
  // Accepts an optional explicit message (quick-prompt chips), otherwise
  // sends the textarea draft.
  onSend: (text?: string) => void;
  onCancelTurn: () => void;
  onStartFresh: () => void;
  onReset: () => void;
  onClearThinking: () => void;
  chatBottomRef: React.MutableRefObject<HTMLDivElement | null>;
}

// The chat pane fills its half of the parent grid (see DiscoveryPane's
// `grid-cols-2`). `min-w-0` is critical: without it, long bubble
// content can force the cell wider and push the left pane into
// overflow / chopped territory.
const CHAT_PANE_CLASSES = "min-w-0 min-h-0 bg-white dark:bg-gray-800 flex flex-col overflow-hidden";

function DiscoverChatPane({
  chat,
  chatLoadError,
  onRetryChatLoad,
  chatInput,
  setChatInput,
  sending,
  endingSession,
  resetting,
  cancellingTurn,
  pendingCount,
  endorsedPendingCount,
  topicLabel,
  onSend,
  onCancelTurn,
  onStartFresh,
  onReset,
  onClearThinking,
  chatBottomRef,
}: DiscoverChatPaneProps) {
  // Which window of static quick-prompts to show; the ↻ button rotates it.
  const [quickPromptPage, setQuickPromptPage] = useState(0);
  // "Start over" turns into a destructive "Reset" (wipes history rather than
  // just starting a fresh session) while internals are revealed. This used to be
  // its own Ctrl+Shift hold, which collided with the reveal chord — reaching for
  // internals armed a destructive button under the cursor. One power mode now,
  // not two gestures. The hold was never the safety anyway: `resetChat` still
  // blocks on a confirm.
  const resetMode = useShowInternals();
  if (chatLoadError) {
    return (
      <aside
        className={`${CHAT_PANE_CLASSES} items-center justify-center gap-2 p-4 text-xs`}
      >
        <AgentLoadError error={chatLoadError} onRetry={onRetryChatLoad} />
      </aside>
    );
  }
  if (!chat) {
    return (
      <aside
        className={`${CHAT_PANE_CLASSES} items-center justify-center text-xs text-gray-500 dark:text-gray-400`}
      >
        Loading chat…
      </aside>
    );
  }
  return (
    <aside
      className={CHAT_PANE_CLASSES}
    >
      {/* Single-row header — matches the Knowledge planner layout
          now that the chat pane is wide enough (~480px). Title sits
          left, actions + chip + status pinned right. */}
      <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-shrink-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
          Chat
          {chat.current_session_number > 1 && (
            <span
              className="ml-1.5 text-[11px] font-normal text-gray-500 dark:text-gray-400"
              title="Earlier sessions are kept, but this conversation starts fresh from here."
            >
              · session {chat.current_session_number}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={resetMode ? onReset : onStartFresh}
            disabled={
              chat.status === 'thinking' ||
              endingSession ||
              resetting ||
              (!resetMode && chat.messages.length === 0)
            }
            title={
              resetMode
                ? 'Reset: wipe ALL chat history for this topic. The keyword pool is NOT touched.'
                : 'Start over: begins a fresh chat session. Earlier messages are kept but no longer part of the conversation; the discovery pool is untouched.'
            }
            className={`px-2 py-0.5 text-[11px] rounded border disabled:opacity-50 ${
              resetMode
                ? 'border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {resetMode
              ? resetting
                ? 'Resetting…'
                : 'Reset'
              : endingSession
                ? 'Starting…'
                : 'Start over'}
          </button>
          <ContextUsageChip
            contextTokens={chat.context_tokens}
            contextWindow={chat.context_window}
          />
          <StageModelsWidget surface="discover_keywords" />
          <AgentStatusChip status={chat.status} />
        </div>
      </header>

      {chat.status === 'failed' && (
        <AgentErrorBanner error={chat.last_error} stage={chat.agent_stage} />
      )}


      <div className="flex-1 min-h-0 p-3 overflow-y-auto space-y-1.5">
        {chat.messages.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-gray-400 italic">
            Tell the agent what to do - e.g. "find all fitness related keywords
            and endorse them", "reject anything that mentions running shoes",
            or "also add some queries about gear maintenance".
          </div>
        ) : (
          chat.messages.map((m) => <ChatBubble key={m.id} message={m} />)
        )}
        {chat.status === 'thinking' && (
          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <span className="italic animate-pulse">Agent is thinking…</span>
            <button
              type="button"
              onClick={onCancelTurn}
              disabled={cancellingTurn}
              className="text-amber-700 dark:text-amber-300 hover:underline not-italic"
              title="Stop at the next safe point. Work already done is kept."
            >
              {cancellingTurn ? 'Canceling…' : 'Cancel'}
            </button>
            <Internals>
            <button
              type="button"
              onClick={onClearThinking}
              className="text-indigo-600 dark:text-indigo-400 hover:underline not-italic"
              title="Force-flip the chat back to idle if it's stuck."
            >
              Stuck? Clear
            </button>
            </Internals>
          </div>
        )}
        <div ref={chatBottomRef} />
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 p-2 flex-shrink-0">
        <QuickPromptBar
          agentPrompts={chat.suggested_prompts}
          staticPool={staticQuickPrompts(
            chat.messages.length,
            pendingCount,
            endorsedPendingCount,
            topicLabel,
          )}
          page={quickPromptPage}
          onCycle={() => setQuickPromptPage((p) => p + 1)}
          onSend={onSend}
          disabled={sending || chat.status === 'thinking'}
        />
        <textarea
          rows={3}
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Ask the agent - e.g. 'endorse all fitness-related keywords' or 'reject anything about commuter bikes'"
          disabled={sending || chat.status === 'thinking'}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
        />
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-[10px] text-gray-400 dark:text-gray-500">
            enter to send · shift+enter for newline
          </div>
          <button
            type="button"
            onClick={() => onSend()}
            disabled={sending || chat.status === 'thinking' || !chatInput.trim()}
            className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {sending || chat.status === 'thinking' ? 'Working…' : 'Send'}
          </button>
        </div>
      </div>
    </aside>
  );
}


function ChatBubble({
  message,
}: {
  message: {
    entity_type: string;
    content: string;
    has_tool_call: boolean;
    created_at: string;
  };
}) {
  const role = (message.entity_type || '').toUpperCase();

  if (role === 'SYSTEM') {
    return (
      <div className="my-1.5 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 rounded p-2 text-xs">
        <div className="flex items-baseline gap-2 mb-1 font-medium text-amber-800 dark:text-amber-300">
          <span>Compacted earlier conversation</span>
          <span className="text-amber-600 dark:text-amber-400 font-normal">
            {_formatChatTime(message.created_at)}
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words text-amber-900 dark:text-amber-200 italic">
          {message.content || '(empty summary)'}
        </div>
      </div>
    );
  }

  const isHuman = role === 'HUMAN';
  const isToolOnly =
    !isHuman && !message.content?.trim() && message.has_tool_call;

  // Tool-only assistant turn → compact action stripe instead of a
  // full bubble. The agent worked (endorse / reject / add hits real
  // DB rows live on the left), but a "(no content)" bubble doesn't
  // earn the same visual weight as a real reply. Multiple of these
  // in a row read as a clean activity log.
  if (isToolOnly) {
    return (
      <div className="flex items-baseline gap-1.5 text-[11px] text-indigo-700 dark:text-indigo-300 italic px-1 py-0.5">
        <span aria-hidden>↻</span>
        <span className="flex-1">Agent updated the keyword pool</span>
        <span className="text-gray-400 dark:text-gray-500 not-italic shrink-0">
          {_formatChatTime(message.created_at)}
        </span>
      </div>
    );
  }

  const bubbleClass = isHuman
    ? 'bg-indigo-600 text-white rounded-br-sm'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm';
  const labelClass = isHuman ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400';
  return (
    <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[90%] rounded-lg px-2 py-1.5 text-sm shadow-sm ${bubbleClass}`}>
        <div className={`flex items-baseline gap-2 mb-0.5 text-[10px] font-medium ${labelClass}`}>
          <span>{isHuman ? 'You' : 'Agent'}</span>
          <span className="opacity-70">{_formatChatTime(message.created_at)}</span>
        </div>
        {/* The agent writes markdown, so render it. Raw, a reply naming your topic
            arrived as "**Age & Life Stage**" with the asterisks showing, and a
            bulleted list arrived as a run of hyphens. What YOU typed is left alone —
            it is not markdown, and asterisks in it are asterisks. */}
        {message.content ? (
          isHuman ? (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          ) : (
            <ChatMarkdown text={message.content} />
          )
        ) : (
          <div className={`text-xs italic ${isHuman ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
            (no reply)
          </div>
        )}
        {message.has_tool_call && !isHuman && (
          <div className="text-[10px] text-indigo-700 dark:text-indigo-300 italic mt-1">
            ↻ updated the keyword pool
          </div>
        )}
      </div>
    </div>
  );
}


// Static quick-prompt fallback, picked by where the user is in the flow.
// Used only when the agent hasn't proposed its own follow-ups for the
// latest turn (see DiscoverKeywordsChatResponse.suggested_prompts).
// Returns the FULL pool of static prompt ideas for the current flow state.
// The chat pane shows a rotating 3-chip window of this via `windowPrompts`,
// so order matters: the highest-value move for the state comes first.
function staticQuickPrompts(
  messageCount: number,
  pendingCount: number,
  endorsedPendingCount: number,
  topicLabel: string
): string[] {
  if (messageCount === 0) {
    // Cold start — the "I felt lost staring at a blank prompt" moment. Shown
    // on a fresh chat regardless of whether a pool exists, so the "where do I
    // start / suggest some keywords" entry points are always here.
    return [
      "I'm not sure where to start - can you help me brainstorm?",
      "What's this topic about?",
      'Suggest 10 keyword ideas to get going',
      'What would a competitor in this space rank for?',
      'What are the main subtopics I should cover?',
      'Give me some long-tail variations to consider',
      'What are people searching for around this topic?',
      'Suggest some beginner-level keywords',
      'Suggest some advanced / expert keywords',
    ];
  }
  if (pendingCount > 0) {
    // An untriaged pool to work through. Lead with the topic relevance filter
    // — the highest-value move right after a Google run dumps off-topic noise.
    return [
      `Endorse the keywords that are strictly about ${topicLabel}`,
      `Reject the ones that aren't about ${topicLabel}`,
      'Group the pending keywords into themes',
      'Endorse the high-intent buyer keywords',
      'Reject the overly broad head terms',
      'Which of these are actually worth keeping?',
      'Endorse the question-style keywords',
      'Reject duplicates and near-duplicates',
      'Flag the ones with commercial intent',
    ];
  }
  // Pool is triaged — pivot to expansion / review.
  return [
    endorsedPendingCount > 0
      ? 'Find more keywords like the ones you endorsed'
      : 'Suggest more keyword ideas to expand the pool',
    "Summarize what we've endorsed so far",
    'What gaps are we missing?',
    'Suggest long-tail variations of what we kept',
    'What related subtopics have we not covered?',
    'Add some question-based keywords',
    'Suggest comparison / "vs" keywords',
    'Suggest some brand or product keywords',
    'What seasonal or trending angles are there?',
  ];
}



function _formatChatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}
